import { useState, useRef, useEffect } from 'react'
import type { LineItem } from '@/types'

/**
 * Shared edit-sync mechanics for an inline line-item row (Equipment / Labor /
 * Materials / Misc / Striping).
 *
 * Fixes the dual-source-of-truth bug proven in `equipment-section.repro.test`:
 * the section tab showed a live recompute of seeded-once local state while Bid
 * Totals summed the store's `lineTotal`, and the two silently diverged. This hook
 * makes the row a well-behaved editor of the store:
 *
 *  1. RE-SYNC (kills the stale-display bug): when the store item changes
 *     externally — e.g. `useMaterialCalculations` rewrites a material quantity, or
 *     `setEstimate` re-seeds line items — the local fields re-sync from the item,
 *     but NEVER while the user is focused in the row (their in-progress edit wins).
 *  2. MERGED DEBOUNCE (kills the dropped-field bug): edits share one timer, but a
 *     commit always sends the FULL current field set, so editing several fields
 *     within the window never drops all-but-the-last.
 *  3. FLUSH ON BLUR AND UNMOUNT (kills the tab-switch-drops-edit bug): the pending
 *     commit fires on blur and on unmount.
 *
 * The caller owns the money formula: `commit(values)` receives the complete,
 * latest local values and persists them (typically the section's `onFieldChange`,
 * which recomputes `lineTotal` from `{ ...item, ...values }` — now never stale
 * because `values` carries every editable field).
 */
export function useLineItemRow<T extends Record<string, number | boolean | string>>(
  item: LineItem,
  seed: (item: LineItem) => T,
  commit: (values: T) => void,
  delay = 600,
) {
  const [values, setValuesState] = useState<T>(() => seed(item))
  const valuesRef = useRef(values)
  valuesRef.current = values
  const focusedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const commitRef = useRef(commit)
  commitRef.current = commit
  const seedRef = useRef(seed)
  seedRef.current = seed

  const setValues = (v: T) => {
    valuesRef.current = v
    setValuesState(v)
  }

  // (1) Re-sync local fields from the store item when it changes — but not while
  // the user is actively editing this row, so we never stomp an in-progress edit.
  useEffect(() => {
    if (focusedRef.current) return
    const next = seedRef.current(item)
    const cur = valuesRef.current
    const changed = Object.keys(next).some((k) => next[k] !== cur[k as keyof T])
    if (changed) setValues(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  const fire = () => commitRef.current(valuesRef.current)

  const schedule = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      fire()
    }, delay)
  }

  const flush = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    fire()
  }

  // (3) Flush a pending edit on unmount (tab switch inside the debounce window).
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
        fire()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // (2) Update one field locally and schedule a full-field commit.
  const setField = <K extends keyof T>(key: K, value: T[K]) => {
    setValues({ ...valuesRef.current, [key]: value } as T)
    schedule()
  }

  // Commit immediately (e.g. a checkbox toggle that should persist without blur).
  const setFieldNow = <K extends keyof T>(key: K, value: T[K]) => {
    setValues({ ...valuesRef.current, [key]: value } as T)
    flush()
  }

  const onFocus = () => {
    focusedRef.current = true
  }
  const onBlur = () => {
    focusedRef.current = false
    flush()
  }

  return { values, setValues, setField, setFieldNow, onFocus, onBlur, flush }
}
